import NextLink from 'next/link';
import React from 'react';

import { Text } from '@/components/shared/element/text';
import { styled } from '@/stitches.config';
import { IThemeLCV } from '@/types/shared';
import { ITest, ITracking } from '@/types/tracking';

const StyledButtonTag = styled('button', {
  border: '0',
  bs: 'none',
  bc: 'transparent',
  padding: '$space-0',
  margin: '$space-0',
  boxSizing: 'border-box',
  us: 'none',
  verticalAlign: 'middle',
  cursor: 'pointer',
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  columnGap: '$space-2',
  textAlign: 'center',
  textDecoration: 'none',
  br: '$rounded-1',
  px: '$space-4',
  py: '$space-1',
  minHeight: '$size-6',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    br: 'inherit',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '$gray400',
    transitionProperty: 'border-color, background-color',
    transitionDelay: 'var(--transition-delay)',
    transitionDuration: 'var(--transition-duration)',
    transitionTimingFunction: 'var(--transition-easing)',
    pe: 'none',
  },
  '& span': {
    pe: 'none',
    marginBottom: '-$space-0-5',
  },
  variants: {
    variant: {
      light: {
        '&::before': {
          borderColor: '$gray400',
          bc: '$white',
        },
        '@hover': {
          '&:hover': {
            '&::before': {
              borderColor: '$gray700',
              bc: '$gray200',
            },
          },
        },
      },
      dark: {
        '&::before': {
          borderColor: '$gray900',
          bc: '$gray900',
        },
        '@hover': {
          '&:hover': {
            '&::before': {
              bc: '$gray200',
              borderColor: '$gray200',
            },
            '& span': {
              color: '$gray900',
            },
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'light',
  },
});

interface ButtonTagProps extends React.ComponentProps<typeof StyledButtonTag> {
  className?: string;
  css?: any;
  label?: string;
  href?: string;
  target?: '_self' | '_blank' | '_parent' | '_top';
  prefetch?: boolean;
  tracking?: ITracking;
  test?: ITest;
  onClick?: () => void;
  theme?: IThemeLCV;
}

export const ButtonTag: React.FC<ButtonTagProps> = ({
  className,
  css,
  label,
  href,
  target,
  prefetch = false,
  tracking,
  test,
  onClick,
  theme = 'light',
}: ButtonTagProps) => {
  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (onClick) {
      if (e.cancelable) {
        e.preventDefault();
      }
      onClick();
    }
  };

  return (
    <>
      {href ? (
        <NextLink href={href} passHref prefetch={prefetch} legacyBehavior>
          <StyledButtonTag
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            variant={theme}
            css={css}
            className={className}
            as="a"
            href={href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            data-track={tracking?.dataTrack}
            data-track-value={tracking?.dataTrackValue}
            data-track-text={tracking?.dataTrackText}
            data-track-url={tracking?.dataTrackUrl}
            data-test={test?.dataTest}
          >
            <Text
              variant={theme === 'dark' ? 'text-white' : 'text-gray-darkest'}
            >
              {label}
            </Text>
          </StyledButtonTag>
        </NextLink>
      ) : (
        <StyledButtonTag
          css={css}
          className={className}
          onClick={handleClick}
          data-track={tracking?.dataTrack}
          data-track-value={tracking?.dataTrackValue}
          data-track-text={tracking?.dataTrackText}
          data-track-url={tracking?.dataTrackUrl}
          data-test={test?.dataTest}
        >
          <Text variant={theme === 'dark' ? 'text-white' : 'text-gray-darkest'}>
            {label}
          </Text>
        </StyledButtonTag>
      )}
    </>
  );
};

ButtonTag.displayName = 'ButtonTag';
